import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import PolicyPipelineCard from '../components/PolicyPipelineCard';

const STATUSES = ['draft', 'in_review', 'published', 'archived'];

const PolicyPipelinePage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewerFilter, setReviewerFilter] = useState('');
  const navigate = useNavigate();

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/pipeline');
      setRows(data || []);
    } catch {
      toast.error('Failed to load policy pipeline');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const policies = useMemo(
    () => rows.flatMap((project) => (project.policies || []).map((policy) => ({ ...policy, project_name: project.project_name }))),
    [rows]
  );

  const projects = useMemo(() => rows.map((row) => ({ id: String(row.project_id), name: row.project_name })), [rows]);

  const reviewers = useMemo(() => {
    const map = new Map();
    policies.forEach((policy) => {
      if (policy.assigned_reviewer_id) {
        map.set(String(policy.assigned_reviewer_id), policy.reviewer_name || `User ${policy.assigned_reviewer_id}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [policies]);

  const filtered = useMemo(
    () =>
      policies.filter((policy) => {
        if (projectFilter && String(policy.project_id) !== projectFilter) return false;
        if (statusFilter && policy.status !== statusFilter) return false;
        if (reviewerFilter && String(policy.assigned_reviewer_id || '') !== reviewerFilter) return false;
        return true;
      }),
    [policies, projectFilter, statusFilter, reviewerFilter]
  );

  const grouped = useMemo(
    () =>
      STATUSES.reduce((acc, status) => {
        acc[status] = filtered.filter((policy) => policy.status === status);
        return acc;
      }, {}),
    [filtered]
  );

  const handleOpen = (policy) => {
    navigate(`/projects/${policy.project_id}/versions/${policy.source_version_id}`);
  };

  return (
    <section className="panel panel--spaced">
      <h1>Policy Pipeline</h1>

      <div className="row-controls">
        <div className="form-group">
          <label htmlFor="pipeline-project">Project</label>
          <select id="pipeline-project" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="">All</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="pipeline-status">Status</label>
          <select id="pipeline-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="pipeline-reviewer">Reviewer</label>
          <select id="pipeline-reviewer" value={reviewerFilter} onChange={(e) => setReviewerFilter(e.target.value)}>
            <option value="">All</option>
            {reviewers.map((reviewer) => (
              <option key={reviewer.id} value={reviewer.id}>{reviewer.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="muted">Loading pipeline...</p>}

      {!loading && (
        <div className="pipeline-grid">
          {STATUSES.map((status) => (
            <div key={status} className="pipeline-column">
              <h3>{status.replace('_', ' ')}</h3>
              {grouped[status]?.length ? (
                grouped[status].map((policy) => <PolicyPipelineCard key={policy.id} policy={policy} onOpen={handleOpen} />)
              ) : (
                <p className="muted">No policies</p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PolicyPipelinePage;
